import {
  ATTRIBUTE_CONDITION_KIND,
  ATTRIBUTE_OBJECTS,
  attributeField,
  CONDITION_FIELDS,
  CONDITION_OPERATORS,
  customerAttributeField,
  customerAttributeKey,
  isFacingTrigger,
  LIST_OPERATORS,
  FIELD_CHOICES,
  parseAttributeField,
  TRIGGER_ONLY_FIELDS,
  VALUELESS_OPERATORS,
  type AttributeObject,
  type ConditionFieldKind,
  type ConditionOperator,
  type WorkflowTrigger,
} from "@sagot/constants";
import { attributesOf, type AttributeDefinition } from "../../../workspace/shared";
import type { ServiceFieldOption } from "../service/service-refs";
import { factGroupOf, factNameOf, isTicketTrigger, serviceFactAvailable, SERVICE_FIELD_CHOICES, TICKET_FACT_GROUPS, ticketFieldKind } from "../service/service-facts";
import type { Condition } from "../types";

export type Definitions = readonly AttributeDefinition[];

/** i18n key of a built-in condition field (`conversation.channel` becomes `conversation_channel`: dots nest keys). */
export const fieldKey = (field: string): string => field.replace(".", "_");

/** The short list of values a fact can take, when it has one (the editor offers them as choices). */
export const choicesOf = (field: string): readonly string[] | undefined => FIELD_CHOICES[field] ?? SERVICE_FIELD_CHOICES[field];

/** The customer's attributes are the visitor attributes the operator pushes: the definition decides what a condition compares. */
function customerAttributeKind(key: string, definitions: Definitions): ConditionFieldKind {
  const definition = definitions.find((item) => item.object === "visitor" && item.key === key);
  return definition ? ATTRIBUTE_CONDITION_KIND[definition.type] : "text";
}

/** What a condition on this field compares; a custom attribute (or a custom field of the tickets) follows its definition. */
export function kindOfField(field: string, definitions: Definitions = [], ticketFields: readonly ServiceFieldOption[] = []): ConditionFieldKind {
  const builtIn = CONDITION_FIELDS[field];
  if (builtIn) return builtIn;
  const custom = ticketFieldKind(field, ticketFields);
  if (custom) return custom;
  const customerKey = customerAttributeKey(field);
  if (customerKey) return customerAttributeKind(customerKey, definitions);
  const attribute = parseAttributeField(field);
  const definition = attribute ? definitions.find((item) => item.object === attribute.object && item.key === attribute.key) : undefined;
  return definition ? ATTRIBUTE_CONDITION_KIND[definition.type] : "text";
}

/** Operators that make sense for a field's kind. */
export function operatorsFor(field: string, definitions: Definitions = [], ticketFields: readonly ServiceFieldOption[] = []): readonly ConditionOperator[] {
  const kind = kindOfField(field, definitions, ticketFields);
  if (kind === "bool") return ["is", "is_set", "is_not_set"];
  if (kind === "number") return ["is", "is_not", "gt", "lt", "is_set", "is_not_set"];
  if (kind === "list") return ["contains", "not_contains", "contains_any_of", "is_set", "is_not_set"];
  return CONDITION_OPERATORS.filter((op) => op !== "gt" && op !== "lt");
}

/** The value a condition carries after a field or operator change (a type that fits). */
export function valueFor(field: string, op: ConditionOperator, raw: Condition["value"], definitions: Definitions = [], ticketFields: readonly ServiceFieldOption[] = []): Condition["value"] {
  if (VALUELESS_OPERATORS.has(op)) return null;
  const choices = choicesOf(field);
  if (choices && !LIST_OPERATORS.has(op)) return typeof raw === "string" && choices.includes(raw) ? raw : (choices[0] ?? "");
  const kind = kindOfField(field, definitions, ticketFields);
  if (kind === "bool") return typeof raw === "boolean" ? raw : true;
  if (LIST_OPERATORS.has(op)) return Array.isArray(raw) ? raw : typeof raw === "string" && raw ? [raw] : [];
  if (kind === "number") return typeof raw === "number" ? raw : Number(raw) || 0;
  return Array.isArray(raw) ? (raw[0] ?? "") : typeof raw === "string" ? raw : "";
}

/** A group of the field picker: built-in fields by subject, then the custom attributes of each object. */
export interface FieldGroup {
  key: string;
  attribute: AttributeObject | null;
  fields: { field: string; label: string | null }[];
}

/** The prefix of fields that read the body of a "Wait for webhook" call. */
export const HOOK_FIELD_PREFIX = "webhook.";

/** The detected intent is read by the visitor-side gate: only workflows on a customer-facing trigger can see it. */
const INTENT_GROUP = "intent";

/**
 * Built-in fields grouped by the part before the dot, then one group per object that has custom attributes, then the
 * run variables the workflow fills and the webhook fields it expects. Given the workflow's `trigger`, fields it can never read are left out.
 */
export function fieldGroups(
  definitions: Definitions = [],
  hookKeys: readonly string[] = [],
  trigger?: WorkflowTrigger,
  runVariables: readonly string[] = [],
  ticketFields: readonly ServiceFieldOption[] = [],
): FieldGroup[] {
  const groups = new Map<string, FieldGroup>();
  const onTicket = isTicketTrigger(trigger);
  for (const field of Object.keys(CONDITION_FIELDS)) {
    const key = field.split(".")[0] ?? field;
    if (key === INTENT_GROUP && trigger !== undefined && !isFacingTrigger(trigger)) continue;
    if (trigger !== undefined && !(TRIGGER_ONLY_FIELDS[field]?.includes(trigger) ?? true)) continue;
    if (!serviceFactAvailable(field, trigger)) continue;
    // A ticket moment has a ticket, its organisation and its requester, not a conversation, a visitor or a lead.
    if (onTicket && !(TICKET_FACT_GROUPS as readonly string[]).includes(key)) continue;
    const group = groups.get(key) ?? { key, attribute: null, fields: [] };
    group.fields.push({ field, label: null });
    groups.set(key, group);
  }
  // The custom fields of the tickets and the attributes of the organisations and contacts join the group of their object.
  if (onTicket) for (const item of ticketFields) groups.get(factGroupOf(item))?.fields.push({ field: factNameOf(item), label: item.name });
  // The attributes the operator pushed for the signed-in customer (`customer.attr.<key>`) are the visitor attributes by another route; they
  // are listed by field name (like run variables and webhook fields) so they never read as the visitor attribute of the same label.
  const customerDefined = onTicket ? [] : attributesOf(definitions, "visitor");
  const customer: FieldGroup[] = customerDefined.length === 0 ? [] : [{ key: "customerAttributes", attribute: null, fields: customerDefined.map((item) => ({ field: customerAttributeField(item.key), label: customerAttributeField(item.key) })) }];
  const custom = ATTRIBUTE_OBJECTS.filter(() => !onTicket).flatMap((object): FieldGroup[] => {
    const defined = attributesOf(definitions, object);
    return defined.length === 0 ? [] : [{ key: object, attribute: object, fields: defined.map((item) => ({ field: attributeField(object, item.key), label: item.label })) }];
  });
  const hook: FieldGroup[] = hookKeys.length === 0 ? [] : [{ key: "webhook", attribute: null, fields: hookKeys.map((key) => ({ field: `${HOOK_FIELD_PREFIX}${key}`, label: `${HOOK_FIELD_PREFIX}${key}` })) }];
  const variables: FieldGroup[] = runVariables.length === 0 ? [] : [{ key: "variable", attribute: null, fields: runVariables.map((field) => ({ field, label: field })) }];
  const builtIn = onTicket ? TICKET_FACT_GROUPS.flatMap((key) => groups.get(key) ?? []) : [...groups.values()];
  return [...builtIn, ...customer, ...custom, ...variables, ...hook];
}

/** Whether the picker lists this field (a deleted attribute stays selectable so the condition is not misread). */
export const isListedField = (field: string, groups: readonly FieldGroup[]): boolean => groups.some((group) => group.fields.some((item) => item.field === field));
